import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { BatchUpsertAvailabilitiesCompactInputDto } from "../../../models/court/dto/batch-upsert-availabilities-compact.dto";
import { SportCapacityPricingWarningDto } from "../../../models/court/dto/capacity-pricing-warning.dto";
import { CommitmentCoverageLossWarningDto } from "../../../models/court/dto/commitment-coverage-loss-warning.dto";
import { CommitmentRepriceWarningDto } from "../../../models/court/dto/commitment-reprice-warning.dto";

/**
 * Sends the compact availability schedule to the server, which expands it into
 * concrete slots. Replaces the old client-side expansion that built tens of
 * thousands of objects in the browser (and crashed the tab / hit the 10MB body
 * limit) when creating availabilities over a long date range.
 */
/**
 * One (sport, capacity) combination whose per-player share the API cannot
 * charge: `price / capacity` lands inside Stripe's 1–49 cent dead zone, so
 * every booking at that capacity is refused (D25). ADDITIVE — an older API
 * omits the field entirely, hence the optional shape.
 */
export type UnchargeableCapacityPrice = {
    sport: string;
    capacity: number;
    price: number;
    perPlayerCents: number;
    fromWildcardRow: boolean;
};

export type BatchUpsertAvailabilitiesCompactResult = {
    count: number;
    unchargeableCapacityPrices?: UnchargeableCapacityPrice[];
    /**
     * What this save took OFF SALE (D136): sizes the organization still offers
     * that no court can price any more, because the save replaced the rows that
     * priced them. A different question from `unchargeableCapacityPrices` above
     * — that one is a price nobody can pay, this one is a size nobody can buy —
     * so the two are rendered as separate sentences in one toast.
     *
     * ADDITIVE and optional, like its sibling: an older API omits it entirely.
     */
    capacityPricingWarnings?: SportCapacityPricingWarningDto[];
    /**
     * What this save changed for bookings that are ALREADY sold: the price
     * their joiners are quoted from. Neither of the two fields above sees it —
     * one is about a price nobody can pay, the other about a size nobody can
     * buy, and this is a price that simply moved under people who already
     * bought.
     *
     * ADDITIVE and optional, like its siblings.
     */
    repricedCommitments?: CommitmentRepriceWarningDto[];
    /**
     * What this save took away from bookings that are ALREADY sold: their price,
     * entirely. The slot is not sold any more at ANY size — the hours narrowed,
     * or the sport came off the court — so every join, quote and replacement
     * redemption on those bookings 404s until it is priced again.
     *
     * The severe end of the same axis as `repricedCommitments`: that one moved a
     * price, this one removed it. ADDITIVE and optional, like its siblings.
     */
    unpricedCommitments?: CommitmentCoverageLossWarningDto[];
};

export const useBatchUpsertAvailabilitiesCompact = (
    courtId: number,
    /**
     * Receives the API's response, so the caller can warn about prices the save
     * accepted but no player will be able to pay.
     */
    onSuccess?: (result: BatchUpsertAvailabilitiesCompactResult) => void,
    /**
     * Receives the `ApiError`, whose `message` is the API's own 400 text. The
     * callback took no arguments before, so the reason a year-long batch was
     * refused could not reach the operator at all.
     */
    onError?: (error: unknown) => void,
) => {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['upsertAvailabilitiesCompact'],
        mutationFn: (input: BatchUpsertAvailabilitiesCompactInputDto) =>
            client.fetchApi<BatchUpsertAvailabilitiesCompactResult, BatchUpsertAvailabilitiesCompactInputDto>({
                method: 'POST',
                endpoint: `courts/${courtId}/availabilities/compact`,
                body: input,
            }),
        onSuccess: (result) => {
            queryClient.invalidateQueries(['courtCalendar', courtId]);
            if (onSuccess) onSuccess(result ?? { count: 0 });
        },
        onError,
    });
}
